import { useEffect, useState } from 'react';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Layout, StripePaymentElementChangeEvent, StripePaymentElementOptions } from '@stripe/stripe-js';
import { usePaymentApiV1 } from '../../../../clients/quipli-service/payment/payment-api-v1';
import { CustomerCustomer } from '../../../../clients/customer-api/customer/customer-customer';
import { Field } from '../../../../components/field/field';
import { Divider } from '../../../../components/divider/divider';
import { EcommerceButton } from '../../../../components/form/button';
import { ecommerceToast } from '../../../../util/toasts';
import { useCustomerAPI } from '../../../../clients/customer-api/customer-api-client';

export default function SetupPaymentForm({
  user,
  baseUrl,
  enableCancel,
  onCancel,
  onSubmit,
  type = 'card',
}: {
  user: CustomerCustomer;
  baseUrl: string;
  enableCancel: boolean;
  onCancel?: () => void;
  onSubmit: () => void;
  type?: 'bankAccount' | 'cardPresent' | 'card';
}) {
  const { saveCard } = usePaymentApiV1();
  const { addBankAccount } = useCustomerAPI();
  const stripe = useStripe();
  const elements = useElements();
  const [cardholderName, setCardholderName] = useState(user.name);
  const [nickname, setNickname] = useState('');
  const [paymentElementComplete, setPaymentElementComplete] = useState(false);
  const [submitState, setSubmitState] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!stripe) {
      return;
    }

    const clientSecret = new URLSearchParams(window.location.search).get('payment_intent_client_secret');

    if (!clientSecret) {
      return;
    }
  }, []);

  useEffect(() => {
    setSubmitState(!(cardholderName != '' && paymentElementComplete));
  }, [cardholderName, paymentElementComplete]);

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();

    if (!stripe || !elements || isSubmitting) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setIsSubmitting(true);

    const { error, setupIntent } = await stripe.confirmSetup({
      //`Elements` instance that was used to create the Payment Element
      elements,
      confirmParams: {
        return_url: `${baseUrl}/cart/save_update_card`,
        payment_method_data: {
          billing_details: {
            name: cardholderName,
          },
        },
      },
      redirect: 'if_required',
    });

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (error) {
      ecommerceToast(
        error.type === 'card_error' || error.type === 'validation_error'
          ? error.message
          : 'An unexpected error occurred.',
        'error',
      );
    }

    if (setupIntent) {
      let paymentMethodId: string;
      let status: string = 'unverified';
      if (typeof setupIntent.payment_method == 'string') {
        paymentMethodId = setupIntent.payment_method;
      } else if (setupIntent.payment_method != null) {
        paymentMethodId = setupIntent.payment_method.id;
      } else {
        if (type == 'bankAccount') {
          paymentMethodId = setupIntent.id;
          status = 'unverified';
        } else {
          throw new Error('No payment method returned');
        }
      }
      if (type == 'card') {
        await saveCard({
          paymentMethod: paymentMethodId,
          cardholderName,
          nickname,
        });
        setIsSubmitting(false);
        onSubmit();
      } else {
        await addBankAccount({
          stripePaymentMethodId: paymentMethodId,
          userId: user.id,
          accountHolderName: cardholderName,
          status,
          gatewayResponse: JSON.stringify(setupIntent),
        });
        setIsSubmitting(false);
        onSubmit();
      }
    } else {
      throw new Error('No setup intent returned');
    }
    document.body.dispatchEvent(new CustomEvent('payment-setup-complete'));
  };

  const paymentElementOptions: StripePaymentElementOptions = {
    layout: { type: 'tabs' as Layout },
    terms: { card: 'never' },
    defaultValues: { billingDetails: { email: user.email } },
  };

  const cancelButton = enableCancel ? (
    <>
      <EcommerceButton type="button" buttonStyle="tertiary" onClick={() => onCancel?.()} disabled={isSubmitting}>
        Cancel
      </EcommerceButton>
    </>
  ) : undefined;

  function checkPaymentElement(event: StripePaymentElementChangeEvent) {
    if (event.complete) {
      setPaymentElementComplete(true);
    }
    toggleSubmit();
  }

  function toggleSubmit() {
    setSubmitState(!(paymentElementComplete && cardholderName != ''));
  }

  return (
    <form id="payment-form">
      {type == 'card' && (
        <div className="flex flex-col md:flex-row justify-between gap-3">
          <Field
            className="grow"
            type="text"
            label="Card nick name"
            labelType="text-xs"
            labelWeight="medium"
            value={nickname}
            onChange={(value: string) => setNickname(value)}
            placeholder="e-g Adam Visa"
          />

          <Field
            className="grow"
            type="text"
            label="Card holder name"
            labelType="text-xs"
            labelWeight="medium"
            value={cardholderName}
            onChange={(value: string) => setCardholderName(value)}
            placeholder="e-g Adam Smith"
          />
        </div>
      )}

      <PaymentElement id="payment-element" options={paymentElementOptions} onChange={checkPaymentElement} />

      <div className="mt-4">
        <Divider />
      </div>

      <div className="flex justify-start gap-2 p-3">
        {cancelButton}
        <EcommerceButton buttonStyle="primary" disabled={submitState || isSubmitting} onClick={handleSubmit}>
          Save Payment
        </EcommerceButton>
      </div>
    </form>
  );
}
